import type {
  BankAccount,
  BankAccountType,
  BankTransaction,
  CreateBankAccountPayload,
  CreateBankTransactionPayload,
} from "@/features/banking/types";
import { apiFetch } from "@/lib/api";

/** Paginated list envelope: `{ success, message, data, meta }`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiBankAccount {
  id: string;
  name: string;
  institution: string;
  account_last4: string;
  account_type: BankAccountType;
}

interface ApiBankTransaction {
  id: string;
  date: string;
  description: string;
  amount: string;
  category: string;
  reconciled: boolean;
}

function toAccount(a: ApiBankAccount): BankAccount {
  return {
    id: a.id,
    name: a.name,
    institution: a.institution ?? "",
    accountLast4: a.account_last4 ?? "",
    accountType: a.account_type,
  };
}

function toTransaction(a: ApiBankTransaction): BankTransaction {
  return {
    id: a.id,
    date: a.date,
    description: a.description,
    amount: a.amount,
    category: a.category ?? "",
    reconciled: a.reconciled,
  };
}

/** GET /clients/:clientId/bank-accounts/ */
export async function listBankAccounts(clientId: string): Promise<BankAccount[]> {
  const body = await apiFetch<Envelope<ApiBankAccount[]>>(
    `/clients/${clientId}/bank-accounts/?page_size=200`,
  );
  return body.data.map(toAccount);
}

/** POST /clients/:clientId/bank-accounts/ */
export async function createBankAccount(
  clientId: string,
  payload: CreateBankAccountPayload,
): Promise<BankAccount> {
  const body = await apiFetch<Envelope<ApiBankAccount>>(
    `/clients/${clientId}/bank-accounts/`,
    { method: "POST", json: payload },
  );
  return toAccount(body.data);
}

/** DELETE /clients/:clientId/bank-accounts/:accountId/ */
export function deleteBankAccount(clientId: string, accountId: string): Promise<void> {
  return apiFetch<void>(`/clients/${clientId}/bank-accounts/${accountId}/`, {
    method: "DELETE",
  });
}

/** GET /clients/:clientId/bank-accounts/:accountId/transactions/ */
export async function listTransactions(
  clientId: string,
  accountId: string,
): Promise<BankTransaction[]> {
  const body = await apiFetch<Envelope<ApiBankTransaction[]>>(
    `/clients/${clientId}/bank-accounts/${accountId}/transactions/?page_size=200`,
  );
  return body.data.map(toTransaction);
}

/** POST /clients/:clientId/bank-accounts/:accountId/transactions/ */
export async function createTransaction(
  clientId: string,
  accountId: string,
  payload: CreateBankTransactionPayload,
): Promise<BankTransaction> {
  const body = await apiFetch<Envelope<ApiBankTransaction>>(
    `/clients/${clientId}/bank-accounts/${accountId}/transactions/`,
    { method: "POST", json: payload },
  );
  return toTransaction(body.data);
}
